import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createBlock as _createBlock, withCtx as _withCtx, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "w-full flex flex-col gap-4" }
const _hoisted_2 = { class: "w-full flex flex-wrap items-center justify-between gap-4" }
const _hoisted_3 = { class: "flex flex-wrap items-center gap-4" }
const _hoisted_4 = { class: "flex-grow min-w-[350px] sm:flex-grow-0 sm:w-2/5" }
const _hoisted_5 = { class: "flex-shrink-0" }
const _hoisted_6 = {
  key: 0,
  class: "my-2 flex flex-wrap gap-2"
}
const _hoisted_7 = {
  key: 0,
  class: "inline-flex items-center rounded-full border border-primary30 px-4 py-2 text-sm text-primary30 bg-primary-50"
}
const _hoisted_8 = {
  key: 1,
  class: "inline-flex items-center rounded-full border border-primary30 px-4 py-2 text-sm text-primary30 bg-primary-50"
}
const _hoisted_9 = {
  key: 1,
  class: "text-center text-red-500 my-4"
}
const _hoisted_10 = { class: "flex flex-col gap-10 sm:min-h-[570px] justify-between" }
const _hoisted_11 = { class: "flex flex-col gap-10 sm:min-h-[570px] justify-between" }
const _hoisted_12 = { class: "flex flex-col gap-4" }
const _hoisted_13 = { class: "w-full md:max-w-[120px] inline md:hidden" }

import { ref, computed, onMounted, onUnmounted, watch, defineAsyncComponent } from 'vue'
import { useDoctorStore } from '@/pinia-store/useDoctorStore'
import { useCompanyStore } from '@/pinia-store/useCompanyStore'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'
import { CompanyDoctorInviteStatusEnum } from '@/Network/Types/Requests/CompanyRequests'
import StatesFromBR from '@/config/StatesFromBR'

import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import Tabs, { type TabItem } from '@/components/designSystem/Tabs/Tabs.vue'
import { CreateInviteModal } from './components/atoms'
import SearchInput from '@/components/designSystem/Inputs/SearchInput.vue'
import SelectFilter from '@/components/Filters/SelectFilter.vue'
import { OutstandingDoctors, CardsTab } from '@/modules/company/Doctors/components/organisms'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import { useHeaderStore } from '@/pinia-store/useHeaderStore'

const DOCTORS_PAGE_LIMIT = 12


export default /*#__PURE__*/_defineComponent({
  __name: 'Index',
  setup(__props) {

const ufIcon = defineAsyncComponent(() => import('@/assets/icons/position-icon.vue'))
const companyIcon = defineAsyncComponent(() => import('@/assets/icons/hospital.vue'))

const doctorStore = useDoctorStore()
const companyStore = useCompanyStore()
const { listCompanies } = storeToRefs(companyStore)
const permissionsStore = usePermissionsStore()
const headerStore = useHeaderStore()

const route = useRoute()

const tabList: TabItem[] = [
  {
    label: 'Meus médicos',
    value: CompanyDoctorInviteStatusEnum.ACCEPTED,
    testId: 'my-doctors-tab-button'
  },
  {
    label: 'Médicos pendentes',
    value: CompanyDoctorInviteStatusEnum.PENDING,
    testId: 'pending-doctors-tab-button'
  }
]

const initialInviteStatus =
  (route.query.inviteStatus as CompanyDoctorInviteStatusEnum) ||
  CompanyDoctorInviteStatusEnum.ACCEPTED
const activeTab = ref<string>(initialInviteStatus)

const currentPageTab1 = ref(1)
const itemsPerPageTab1 = ref(15)

const searchInput = ref('')
const createInviteModalProps = ref(false)

const activeFilter = ref<string | null>(null)
const selectedCrmUf = ref<string | null>(null)
const selectedCompanyId = ref<string | null>(null)
const selectedCompanyLabel = ref<string | null>(null)

const crmUfOptions = computed(() =>
  StatesFromBR.map((uf) => ({ label: uf.title, value: uf.value }))
)

const companyOptions = computed(() =>
  listCompanies.value.map((company) => ({
    label: company.title,
    value: String(company.value)
  }))
)

const selectedCrmUfLabel = computed(() => {
  const selected = crmUfOptions.value.find((opt) => opt.value === selectedCrmUf.value)
  return selected ? selected.label : null
})

onMounted(async () => {
  headerStore.setTitle(
    'Médicos',
    'Gerencie médicos vinculados às empresas e acompanhe convites pendentes'
  )
  await doctorStore.applyFilters(
    {
      search: '',
      names: [],
      crms: [],
      crmUFs: [],
      companies: [],
      inviteStatus: activeTab.value as CompanyDoctorInviteStatusEnum,
      limit: DOCTORS_PAGE_LIMIT
    },
    false
  )

  if (listCompanies.value.length === 0) {
    await companyStore.getMyCompanies()
  }
})

const pageCountTab1 = computed(() => {
  if (!doctorStore.doctorQuery.limit) return 1
  return Math.ceil(doctorStore.totalDoctors / doctorStore.doctorQuery.limit)
})

const changePageTab1 = (page: number) => {
  currentPageTab1.value = page
  doctorStore.changePage(page)
}

const closeCreateInviteModal = () => {
  createInviteModalProps.value = false
}

let searchDebounceTimer: number
const changeSearchInput = () => {
  clearTimeout(searchDebounceTimer)
  searchDebounceTimer = window.setTimeout(() => {
    doctorStore.applyFilters({ search: searchInput.value, limit: DOCTORS_PAGE_LIMIT })
    currentPageTab1.value = 1
  }, 500)
}

function toggleFilter(filterName: string) {
  if (activeFilter.value === filterName) {
    activeFilter.value = null
  } else {
    activeFilter.value = filterName
  }
}

const filtersToolbarRef = ref<HTMLElement | null>(null)

// Fecha os dropdowns de filtro ao clicar fora da toolbar. Menus do Vuetify
// são teleportados para o overlay — ignorar.
function handleDocumentClick(event: MouseEvent) {
  const target = event.target as HTMLElement | null
  if (!target || target.closest('.v-overlay-container')) return
  if (filtersToolbarRef.value && !filtersToolbarRef.value.contains(target)) {
    activeFilter.value = null
  }
}

onMounted(() => document.addEventListener('click', handleDocumentClick))
onUnmounted(() => document.removeEventListener('click', handleDocumentClick))

function handleCrmUfSelect(value: string) {
  selectedCrmUf.value = value
  doctorStore.applyFilters({ crmUFs: [value], limit: DOCTORS_PAGE_LIMIT })
  currentPageTab1.value = 1
  activeFilter.value = null
}

function handleCompanySelect(value: string) {
  const selected = companyOptions.value.find((opt) => opt.value === value)
  if (selected) {
    selectedCompanyId.value = selected.value
    selectedCompanyLabel.value = selected.label
    doctorStore.applyFilters({ companies: [selected.value], limit: DOCTORS_PAGE_LIMIT })
    currentPageTab1.value = 1
    activeFilter.value = null
  }
}

function clearCrmUfFilter() {
  selectedCrmUf.value = null
  doctorStore.applyFilters({ crmUFs: [], limit: DOCTORS_PAGE_LIMIT })
  currentPageTab1.value = 1
}

function clearCompanyFilter() {
  selectedCompanyId.value = null
  selectedCompanyLabel.value = null
  doctorStore.applyFilters({ companies: [], limit: DOCTORS_PAGE_LIMIT })
  currentPageTab1.value = 1
}

function clearAllFilters() {
  searchInput.value = ''
  clearCrmUfFilter()
  clearCompanyFilter()
}

watch(activeTab, (newTab) => {
  doctorStore.applyFilters({
    inviteStatus: newTab as CompanyDoctorInviteStatusEnum,
    limit: DOCTORS_PAGE_LIMIT
  })
  currentPageTab1.value = 1
})

const canCreateInvite = computed(() => {
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.CompanyDoctorInviteCreating,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  })
})

return (_ctx: any,_cache: any) => {
  const _component_v_pagination = _resolveComponent("v-pagination")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _createVNode(SearchInput, {
              modelValue: searchInput.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchInput).value = $event)),
              width: "100%",
              label: "Buscar pelo nome, CRM ou CRMUF",
              onInput: changeSearchInput,
              density: "compact",
              "hide-details": ""
            }, null, 8, ["modelValue"])
          ]),
          _createElementVNode("div", {
            ref_key: "filtersToolbarRef",
            ref: filtersToolbarRef,
            class: "flex flex-wrap gap-2"
          }, [
            _createVNode(SelectFilter, {
              label: "CRMUF",
              icon: _unref(ufIcon),
              options: crmUfOptions.value,
              "is-open": activeFilter.value === 'crmUf',
              "dropdown-width": "w-56",
              onSelect: handleCrmUfSelect,
              onClick: _cache[1] || (_cache[1] = ($event: any) => (toggleFilter('crmUf')))
            }, null, 8, ["icon", "options", "is-open"]),
            _createVNode(SelectFilter, {
              label: "Empresa",
              icon: _unref(companyIcon),
              options: companyOptions.value,
              "is-open": activeFilter.value === 'company',
              searchable: "",
              "dropdown-width": "w-64",
              onSelect: handleCompanySelect,
              onClick: _cache[2] || (_cache[2] = ($event: any) => (toggleFilter('company')))
            }, null, 8, ["icon", "options", "is-open"])
          ], 512)
        ]),
        _createElementVNode("div", _hoisted_5, [
          _createVNode(FilledButton, {
            onClick: _cache[3] || (_cache[3] = () => (createInviteModalProps.value = true)),
            text: "Criar convite",
            width: "160px",
            height: "40px",
            class: "!bg-primary30",
            disabled: !canCreateInvite.value
          }, null, 8, ["disabled"])
        ])
      ]),
      (selectedCrmUfLabel.value || selectedCompanyLabel.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
            (selectedCrmUfLabel.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_7, [
                  _createTextVNode(" CRMUF: " + _toDisplayString(selectedCrmUfLabel.value) + " ", 1),
                  _createElementVNode("button", {
                    onClick: clearCrmUfFilter,
                    class: "ml-2 text-primary-500 hover:text-primary-700 font-bold focus:outline-none"
                  }, " x ")
                ]))
              : _createCommentVNode("", true),
            (selectedCompanyLabel.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_8, [
                  _createTextVNode(" Empresa: " + _toDisplayString(selectedCompanyLabel.value) + " ", 1),
                  _createElementVNode("button", {
                    onClick: clearCompanyFilter,
                    class: "ml-2 text-primary-500 hover:text-primary-700 font-bold focus:outline-none"
                  }, " x ")
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("button", {
              onClick: clearAllFilters,
              class: "text-sm font-bold text-primary hover:text-primary-darken-1 transition-colors"
            }, " Limpar filtros ")
          ]))
        : _createCommentVNode("", true),
      (_unref(doctorStore).isError)
        ? (_openBlock(), _createElementBlock("div", _hoisted_9, " Erro ao carregar dados: " + _toDisplayString(_unref(doctorStore).errorMessage), 1))
        : _createCommentVNode("", true),
      _createVNode(Tabs, {
        modelValue: activeTab.value,
        "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((activeTab).value = $event)),
        tabs: tabList
      }, {
        ACCEPTED: _withCtx(() => [
          _createElementVNode("div", _hoisted_10, [
            _createVNode(_unref(CardsTab), {
              companyDoctors: _unref(doctorStore).doctorsCompany || [],
              isLoading: _unref(doctorStore).isLoading
            }, null, 8, ["companyDoctors", "isLoading"]),
            (!_unref(doctorStore).isLoading && (_unref(doctorStore).doctorsCompany?.length || 0) > 0)
              ? (_openBlock(), _createBlock(_component_v_pagination, {
                  key: 0,
                  modelValue: currentPageTab1.value,
                  "onUpdate:modelValue": [
                    _cache[4] || (_cache[4] = ($event: any) => ((currentPageTab1).value = $event)),
                    changePageTab1
                  ],
                  "total-visible": itemsPerPageTab1.value,
                  length: pageCountTab1.value,
                  density: "comfortable",
                  color: "primary"
                }, null, 8, ["modelValue", "total-visible", "length"]))
              : _createCommentVNode("", true)
          ])
        ]),
        PENDING: _withCtx(() => [
          _createElementVNode("div", _hoisted_11, [
            _createVNode(_unref(OutstandingDoctors), {
              companyDoctors: _unref(doctorStore).doctorsCompany || [],
              isLoading: _unref(doctorStore).isLoading
            }, null, 8, ["companyDoctors", "isLoading"]),
            _createElementVNode("div", _hoisted_12, [
              (!_unref(doctorStore).isLoading && (_unref(doctorStore).doctorsCompany?.length || 0) > 0)
                ? (_openBlock(), _createBlock(_component_v_pagination, {
                    key: 0,
                    modelValue: currentPageTab1.value,
                    "onUpdate:modelValue": [
                      _cache[5] || (_cache[5] = ($event: any) => ((currentPageTab1).value = $event)),
                      changePageTab1
                    ],
                    "total-visible": itemsPerPageTab1.value,
                    length: pageCountTab1.value,
                    density: "comfortable",
                    color: "primary"
                  }, null, 8, ["modelValue", "total-visible", "length"]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_13, [
                _createVNode(FilledButton, {
                  onClick: _cache[6] || (_cache[6] = () => (createInviteModalProps.value = true)),
                  text: "Criar convite",
                  width: "160px",
                  height: "40px",
                  class: "!bg-primary30"
                })
              ])
            ])
          ])
        ]),
        _: 1
      }, 8, ["modelValue"])
    ]),
    _createVNode(_unref(CreateInviteModal), {
      modalStateProp: createInviteModalProps.value,
      updateListFunction: _unref(doctorStore).fetchDoctors,
      onClose: closeCreateInviteModal
    }, null, 8, ["modalStateProp", "updateListFunction"])
  ], 64))
}
}

})